<script setup>
import { useFormat } from '@/composables/useFormat'
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'StockOpname',
  },
})


const ability = useAbility()
const { formatDate } = useFormat()

// ─── State ────────────────────────────────────────────────────────────────────
const opnames = ref([])
const totalItems = ref(0)
const isFetching = ref(false)

const options = ref({ page: 1, itemsPerPage: 15 })
const filterStatus = ref(null)

const statusOptions = [
  { title: 'Semua Status', value: null },
  { title: 'Draft', value: 'draft' },
  { title: 'Dikonfirmasi', value: 'confirmed' },
  { title: 'Dibatalkan', value: 'cancelled' },
]

const headers = [
  { title: 'No. Opname', key: 'opname_number' },
  { title: 'Tanggal', key: 'opname_date' },
  { title: 'Status', key: 'status' },
  { title: 'Total Item', key: 'total_items', align: 'center' },
  { title: 'Ada Selisih', key: 'items_with_diff', align: 'center' },
  { title: 'Dibuat Oleh', key: 'creator', sortable: false },
  { title: 'Dikonfirmasi', key: 'confirmed_by', sortable: false },
  { title: 'Aksi', key: 'actions', sortable: false, align: 'center', width: '80px' },
]
 
// ─── Fetch ────────────────────────────────────────────────────────────────────
const fetchOpnames = async () => {
  isFetching.value = true
  try {
    const params = new URLSearchParams({
      page: options.value.page,
      per_page: options.value.itemsPerPage,
    })
    if (filterStatus.value) params.set('status', filterStatus.value)

    const response = await useApi(`/api/v1/stock-opnames?${params}`).get()
    
    const data = response?.data ?? response
    
    opnames.value = data?.data ?? []
    
    totalItems.value = data?.pagination?.total ?? 0
    
  } catch (err) {
    console.error('❌ fetchOpnames:', err)
  } finally {
    isFetching.value = false
  }
}

watch([options, filterStatus], fetchOpnames, { deep: true })
onMounted(fetchOpnames)
 
// ─── Helpers ─────────────────────────────────────────────────────────────────
const statusColor = (s) => ({ draft: 'warning', confirmed: 'success', cancelled: 'error' }[s] ?? 'default')
const statusLabel = (s) => ({ draft: 'Draft', confirmed: 'Dikonfirmasi', cancelled: 'Dibatalkan' }[s] ?? s)
</script>

<template>
  <VRow>
    <VCol cols="12">
      <div class="d-flex align-center justify-space-between mb-6">
        <div>
          <h4 class="text-h4 font-weight-bold">Stock Opname</h4>
          <p class="text-body-2 text-medium-emphasis mt-1">Penghitungan stok fisik bahan baku</p>
        </div>
        <VBtn
          v-if="ability.can('create', 'StockOpname')"
          color="primary"
          prepend-icon="ri-add-line"
          :to="{ name: 'inventory-stock-opnames-create' }"
        >
          Buat Opname Baru
        </VBtn>
      </div>

      <!-- Filter -->
      <VCard class="mb-4">
        <VCardText class="py-3">
          <VRow>
            <VCol cols="12" md="3">
              <VSelect
                v-model="filterStatus"
                :items="statusOptions"
                label="Filter Status"
                density="compact"
                variant="outlined"
                hide-details
              />
            </VCol>
          </VRow>
        </VCardText>
      </VCard>

      <!-- Table -->
      <VCard>
        <VDataTableServer
          v-model:options="options"
          :headers="headers"
          :items="opnames"
          :items-length="totalItems"
          :loading="isFetching"
          class="text-no-wrap"
        >
          <template #item.opname_number="{ item }">
            <span class="font-weight-semibold text-primary">{{ item.opname_number }}</span>
          </template>

          <template #item.opname_date="{ item }">
            {{ formatDate(item.opname_date) }}
          </template>

          <template #item.status="{ item }">
            <VChip :color="statusColor(item.status)" size="small" label>
              {{ statusLabel(item.status) }}
            </VChip>
          </template>

          <template #item.total_items="{ item }">
            <VChip size="small" variant="tonal">{{ item.total_items ?? 0 }}</VChip>
          </template>

          <template #item.items_with_diff="{ item }">
            <VChip
              :color="(item.items_with_diff ?? 0) > 0 ? 'error' : 'success'"
              size="small"
              variant="tonal"
            >
              {{ item.items_with_diff ?? 0 }}
            </VChip>
          </template>

          <template #item.creator="{ item }">
            <span class="text-body-2">{{ item.creator?.name ?? '-' }}</span>
          </template>

          <template #item.confirmed_by="{ item }">
            <span class="text-body-2">{{ item.confirmed_by?.name ?? '-' }}</span>
          </template>

          <template #item.actions="{ item }">
            <VBtn
              icon
              variant="text"
              size="small"
              color="primary"
              :to="{ name: 'inventory-stock-opnames-id', params: { id: item.id } }"
            >
              <VIcon icon="ri-eye-line" size="18" />
              <VTooltip activator="parent">Detail</VTooltip>
            </VBtn>
          </template>

          <template #no-data>
            <div class="text-center py-10">
              <VIcon size="56" color="secondary" icon="ri-clipboard-line" class="mb-3" />
              <p class="text-body-1 text-medium-emphasis mb-1">Belum ada sesi stock opname</p>
              <p class="text-body-2 text-disabled mb-4">Buat sesi pertama untuk menghitung stok fisik</p>
              <VBtn
                v-if="ability.can('create', 'StockOpname')"
                color="primary"
                size="small"
                prepend-icon="ri-add-line"
                :to="{ name: 'inventory-stock-opnames-create' }"
              >
                Buat Opname Baru
              </VBtn>
            </div>
          </template>
        </VDataTableServer>
      </VCard>
    </VCol>
  </VRow>
</template>
